import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useEmberStore } from '../lib/store';
import { toast } from '../lib/toast';
import { compressImage, isImageFile } from '../lib/imageCompression';
import BottomNav from '../components/BottomNav';
import Button from '../components/ui/Button';
import { ArrowLeft, Image as ImageIcon } from 'lucide-react';
import type { LucideProps } from 'lucide-react';
import { loadDraftState, saveDraftState, clearDraftState } from '../lib/draftStateStore';
import { dataUrlToFile } from '../lib/draftPhotoStore';
import { usePhotoStorage, StoredPhoto } from '../hooks/usePhotoStorage';
import { usePhotoProcessor } from '../hooks/usePhotoProcessor';
import { formatDateForInput } from '../lib/dateUtils';

const iconStroke = 1.8;

const TobaccoLeafIcon = ({ size = 24, color = 'currentColor', ...rest }: LucideProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color ?? 'currentColor'}
    strokeWidth={iconStroke}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...rest}
  >
    <path d="M12 2c-4.8 3-7.5 6.6-7.5 10.5S7.6 22 12 22s7.5-3.9 7.5-9.5S16.8 5.1 12 2Z" />
    <path d="M12 2v20" />
    <path d="M8.3 12.3c2 .6 3.7 2.2 3.9 4.2" />
    <path d="M15.7 12.3c-2 .6-3.7 2.2-3.9 4.2" />
  </svg>
);

const PipeIcon = ({ size = 24, color = 'currentColor', ...rest }: LucideProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color ?? 'currentColor'}
    strokeWidth={iconStroke}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...rest}
  >
    <path d="M4 12.5v1.4c0 4 3.2 7.1 7.1 7.1h2.1c4.9 0 8.8-3.8 8.8-8.6V7" />
    <path d="M16 7v3.2a3.7 3.7 0 0 1-3.7 3.7H9" />
    <path d="M12 7V4.5" />
    <path d="M7.6 7c0-1.6 1.2-3.2 3-3.7" />
  </svg>
);

const LighterIcon = ({ size = 24, color = 'currentColor', ...rest }: LucideProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color ?? 'currentColor'}
    strokeWidth={iconStroke}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...rest}
  >
    <path d="M8.2 21h7.6a1.2 1.2 0 0 0 1.2-1.2V13a1.2 1.2 0 0 0-1.2-1.2H8.2A1.2 1.2 0 0 0 7 13v6.8A1.2 1.2 0 0 0 8.2 21Z" />
    <path d="M15 11.8V8h4.2" />
    <path d="M7 11.8V9.2A2.2 2.2 0 0 1 9.2 7H12" />
    <path d="M12 7c0-1.8 1.1-3.5 3.2-5 0 1.4.7 2.6 1.6 3.6.7.8 1 1.6 1 2.5 0 1.6-1.3 2.9-2.9 2.9S12 8.6 12 7Z" />
  </svg>
);

const MatchIcon = ({ size = 24, color = 'currentColor', ...rest }: LucideProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color ?? 'currentColor'}
    strokeWidth={iconStroke}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...rest}
  >
    <path d="M9 21V13.5c0-.9.6-1.7 1.4-2.1l5.2-2.4" />
    <path d="M15 21v-7.8" />
    <path d="M15.6 9c0 2.1-1.6 3.7-3.6 3.7S8.4 11.1 8.4 9c0-1.4.7-2.7 1.9-3.9.7-.7 1-1.6 1-2.5 0-.7-.2-1.4-.6-1.9 2.1.7 5.1 3 5.1 6.3Z" />
    <path d="M6 21l4-4" />
  </svg>
);

type ItemType = 'tobacco' | 'pipe' | 'lighter' | 'matches' | null;

const CELLAR_DRAFT_KEY = 'ember.cellarDraft.v1';

interface CellarDraftStored {
  selectedType: ItemType;
  brand: string;
  blendName: string;
  amount: string;
  unit: 'grams' | 'ounces';
  multiple: string;
  purchaseDate: string;
  tinnedDate: string;
  status: 'sealed' | 'opened' | 'finished';
  storageType: '' | 'tin' | 'jar' | 'pouch' | 'bulk';
  notes: string;
  maker: string;
  shape: string;
  style: string;
  lighterBrand: string;
  lighterStyle: string;
  colour: string;
  photoPreview: string | null;
  photoName: string | null;
  photoType: string | null;
}

export default function AddCellarItem() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useEmberStore();
  const draftLoadedRef = useRef(false);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const urlBatchIdRef = useRef<string | null>(null);
  const existingItemIdRef = useRef<string | null>(null);
  const { loadPhoto, loadPhotoSync, savePhoto, savePhotoData, clearPhoto, loadPhotoFromBackup } = usePhotoStorage(CELLAR_DRAFT_KEY);
  const { processPhoto } = usePhotoProcessor(CELLAR_DRAFT_KEY);
  const initialStoredPhoto = loadPhotoSync();
  const [photoData, setPhotoData] = useState<StoredPhoto | null>(initialStoredPhoto);
  const [photoFile, setPhotoFile] = useState<File | null>(() =>
    initialStoredPhoto ? dataUrlToFile(initialStoredPhoto.data, initialStoredPhoto.name, initialStoredPhoto.type) : null
  );
  const [photoPreview, setPhotoPreview] = useState<string | null>(initialStoredPhoto ? initialStoredPhoto.data : null);

  // Selection state
  const [selectedType, setSelectedType] = useState<ItemType>(null);
  
  // Common states
  const [loading, setLoading] = useState(false);
  const [compressing, setCompressing] = useState(false);
  // Pipe cropper state (2:1 aspect)
  const [showPipeCropper, setShowPipeCropper] = useState(false);
  const [cropImageUrl, setCropImageUrl] = useState<string | null>(null);
  const [cropImgEl, setCropImgEl] = useState<HTMLImageElement | null>(null);
  const [cropScale, setCropScale] = useState(1);
  const [cropOffset, setCropOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState<{x:number;y:number}|null>(null);
  const [cropBaseScale, setCropBaseScale] = useState(1);
  const cropContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // When crop modal opens and image/container are ready, compute the base scale
    if (showPipeCropper && cropImgEl && cropContainerRef.current) {
      const rect = cropContainerRef.current.getBoundingClientRect();
      const base = Math.max(rect.width / cropImgEl.width, rect.height / cropImgEl.height);
      setCropBaseScale(base);
      setCropScale(1);
      setCropOffset({ x: 0, y: 0 });
    }
  }, [showPipeCropper, cropImgEl]);
  
  // Tobacco states
  const [brand, setBrand] = useState('');
  const [blendName, setBlendName] = useState('');
  const [amount, setAmount] = useState('');
  const [unit, setUnit] = useState<'grams' | 'ounces'>('grams');
  const [multiple, setMultiple] = useState<string>('1');
  const [purchaseDate, setPurchaseDate] = useState(formatDateForInput());
  const [tinnedDate, setTinnedDate] = useState('');
  const [status, setStatus] = useState<'sealed' | 'opened' | 'finished'>('sealed');
  const [storageType, setStorageType] = useState<'tin' | 'jar' | 'pouch' | 'bulk' | ''>('');
  const [notes, setNotes] = useState('');
  // Autosuggest state
  const [brandSuggestions, setBrandSuggestions] = useState<string[]>([]);
  const [showBrandSug, setShowBrandSug] = useState(false);
  const [blendSuggestions, setBlendSuggestions] = useState<Array<{ name: string; brand: string | null }>>([]);
  const [showBlendSug, setShowBlendSug] = useState(false);
  const brandTimer = useRef<number | null>(null);
  const blendTimer = useRef<number | null>(null);
  
  // Pipe states
  const [maker, setMaker] = useState('');
  const [shape, setShape] = useState('');
  const [style, setStyle] = useState('');
  
  // Lighter/Matches states
  const [lighterBrand, setLighterBrand] = useState('');
  const [lighterStyle, setLighterStyle] = useState('');
  const [colour, setColour] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stored = await loadDraftState<CellarDraftStored>(CELLAR_DRAFT_KEY);
        if (cancelled) {
          return;
        }
        if (stored) {
          if (stored.selectedType === 'tobacco' || stored.selectedType === 'pipe' || stored.selectedType === 'lighter' || stored.selectedType === 'matches' || stored.selectedType === null) {
            setSelectedType(stored.selectedType);
          }
          if (typeof stored.brand === 'string') setBrand(stored.brand);
          if (typeof stored.blendName === 'string') setBlendName(stored.blendName);
          if (typeof stored.amount === 'string') setAmount(stored.amount);
          if (stored.unit === 'grams' || stored.unit === 'ounces') setUnit(stored.unit);
          if (typeof stored.multiple === 'string') setMultiple(stored.multiple);
          if (typeof stored.purchaseDate === 'string') setPurchaseDate(stored.purchaseDate);
          if (typeof stored.tinnedDate === 'string') setTinnedDate(stored.tinnedDate);
          if (stored.status === 'sealed' || stored.status === 'opened' || stored.status === 'finished') setStatus(stored.status);
          if (stored.storageType === '' || stored.storageType === 'tin' || stored.storageType === 'jar' || stored.storageType === 'pouch' || stored.storageType === 'bulk') setStorageType(stored.storageType);
          if (typeof stored.notes === 'string') setNotes(stored.notes);
          if (typeof stored.maker === 'string') setMaker(stored.maker);
          if (typeof stored.shape === 'string') setShape(stored.shape);
          if (typeof stored.style === 'string') setStyle(stored.style);
          if (typeof stored.lighterBrand === 'string') setLighterBrand(stored.lighterBrand);
          if (typeof stored.lighterStyle === 'string') setLighterStyle(stored.lighterStyle);
          if (typeof stored.colour === 'string') setColour(stored.colour);
        }

        const restored = await loadPhotoFromBackup();
        if (!cancelled) {
          setPhotoData(restored);
        }
      } catch (error) {
        console.warn('Failed to load cellar draft', error);
      } finally {
        if (!cancelled) {
          draftLoadedRef.current = true;
          setDraftLoaded(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadPhotoFromBackup]);

  // Read URL parameters and pre-fill form (prioritize over draft data)
  useEffect(() => {
    // Wait for draft to load first, then apply URL params (which take priority)
    if (!draftLoaded) {
      return; // Wait for draft loading to complete
    }

    const urlType = searchParams.get('type');
    const urlBrand = searchParams.get('brand');
    const urlBlend = searchParams.get('blend');
    const urlAmount = searchParams.get('amount');
    const urlUnit = searchParams.get('unit');
    const urlStorage = searchParams.get('storage');
    const urlBatchId = searchParams.get('batch_id');
    const urlExistingItemId = searchParams.get('existing_item_id');
    
    // Store batch_id for later use in submission (check for empty string)
    if (urlBatchId && urlBatchId.trim() !== '') {
      urlBatchIdRef.current = urlBatchId;
    }
    
    // Store existing_item_id for later use (when adding to a single item)
    if (urlExistingItemId && urlExistingItemId.trim() !== '') {
      existingItemIdRef.current = urlExistingItemId;
    }
    
    // Only process if URL params exist and type is tobacco
    if (!urlType || urlType !== 'tobacco') return;
    
    // If URL params exist, prioritize them over draft data
    // Set type
    setSelectedType('tobacco');
    
    // Pre-fill fields from URL params (override draft data if URL params exist)
    if (urlBrand) {
      setBrand(urlBrand);
    }
    if (urlBlend) {
      setBlendName(urlBlend);
    }
    if (urlAmount) {
      setAmount(urlAmount);
    }
    if (urlUnit === 'grams' || urlUnit === 'ounces') {
      setUnit(urlUnit as 'grams' | 'ounces');
    }
    if (urlStorage && (urlStorage === 'tin' || urlStorage === 'jar' || urlStorage === 'pouch' || urlStorage === 'bulk')) {
      setStorageType(urlStorage as 'tin' | 'jar' | 'pouch' | 'bulk');
    }
  }, [draftLoaded, searchParams]); // Run after draft loads and when URL params change

  useEffect(() => {
    let cancelled = false;

    if (!photoData) {
      setPhotoPreview(null);
      setPhotoFile(null);
      return;
    }

    setPhotoPreview(photoData.data);
    const restoredFile = dataUrlToFile(
      photoData.data,
      (photoData.name && photoData.name.trim()) ? photoData.name : 'cellar-photo.jpg',
      photoData.type || 'image/jpeg',
    );
    setPhotoFile(restoredFile);

    if (!photoData.processed && selectedType !== 'pipe') {
      setCompressing(true);
      processPhoto()
        .then((processed) => {
          if (!cancelled && processed) {
            setPhotoData(processed);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setCompressing(false);
          }
        });
    }

    return () => {
      cancelled = true;
    };
  }, [photoData, processPhoto, selectedType]);

  // Set default unit from user preference
  useEffect(() => {
    if (currentUser?.weight_unit) {
      setUnit(currentUser.weight_unit);
    }
    // Prefill from wish list via query params
    const url = new URL(window.location.href);
    if (url.searchParams.get('prefill') === '1' && !selectedType) {
      const b = url.searchParams.get('brand') || '';
      const bl = url.searchParams.get('blend') || '';
      const img = url.searchParams.get('image') || '';
      const n = url.searchParams.get('notes') || '';
      if (b || bl || img || n) {
        setSelectedType('tobacco');
        setBrand(b);
        setBlendName(bl);
        setNotes(n);
        if (img) setPhotoPreview(img);
      }
    }
  }, [currentUser]);

  useEffect(() => {
    if (!draftLoadedRef.current) {
      return;
    }
    const draft: CellarDraftStored = {
      selectedType,
      brand,
      blendName,
      amount,
      unit,
      multiple,
      purchaseDate,
      tinnedDate,
      status,
      storageType,
      notes,
      maker,
      shape,
      style,
      lighterBrand,
      lighterStyle,
      colour,
      photoPreview: photoData?.data ?? null,
      photoName: photoData?.name ?? null,
      photoType: photoData?.type ?? null,
    };
    void saveDraftState(CELLAR_DRAFT_KEY, draft);
  }, [
    amount,
    brand,
    blendName,
    colour,
    lighterBrand,
    lighterStyle,
    maker,
    multiple,
    notes,
    photoData,
    purchaseDate,
    tinnedDate,
    selectedType,
    shape,
    status,
    storageType,
    style,
    unit,
  ]);

  async function handlePhotoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) {
      setPhotoData(null);
      setPhotoPreview(null);
      setPhotoFile(null);
      clearPhoto();
      return;
    }

    if (!isImageFile(file)) {
      toast.error('Please select a valid image file');
      return;
    }

    if (selectedType === 'pipe') {
      setCompressing(true);
      try {
        const stored = await savePhoto(file);
        if (!stored) {
          setCompressing(false);
          return;
        }
        setPhotoData(stored);
        setCropImageUrl(stored.data);
        const img = new Image();
        img.onload = () => {
          setCropImgEl(img);
          setCropScale(1);
          setCropOffset({ x: 0, y: 0 });
          setShowPipeCropper(true);
          setCompressing(false);
        };
        img.onerror = () => {
          toast.error('Failed to load image for cropping');
          setCompressing(false);
        };
        img.src = stored.data;
      } catch (error) {
        console.error('Error preparing pipe photo:', error);
        toast.error('Failed to process image');
        setCompressing(false);
      }
      return;
    }

    setCompressing(true);
    try {
      const stored = await savePhoto(file);
      if (stored) {
        setPhotoData(stored);
      }
    } catch (error) {
      console.error('Error saving image:', error);
      toast.error('Failed to process image');
    } finally {
      setCompressing(false);
    }
  }

  async function uploadPhoto(table: 'tobacco' | 'pipes' | 'lighters'): Promise<string | null> {
    if (!photoFile || !currentUser) return null;

    const filePath = `${currentUser.id}/${Date.now()}-${photoFile.name}`;
    const { data, error } = await supabase.storage
      .from('cellar-images')
      .upload(filePath, photoFile, { upsert: false });

    if (error || !data) {
      console.error('Error uploading photo:', error);
      return null;
    }

    const { data: pub } = supabase.storage
      .from('cellar-images')
      .getPublicUrl(data.path);

    return pub?.publicUrl ?? null;
  }

  async function handleSubmitTobacco(e: React.FormEvent) {
    e.preventDefault();
    if (!currentUser) return;

    setLoading(true);
    try {
      let image_url = await uploadPhoto('tobacco');
      const parsedQty = parseInt(multiple, 10);
      const qty = Math.max(1, Math.min(Number.isNaN(parsedQty) ? 1 : parsedQty, 100));
      const parsedAmount = parseFloat(amount);
      if (Number.isNaN(parsedAmount) || parsedAmount <= 0) {
        throw new Error('Please enter a valid amount');
      }

      // Handle adding to existing single item: create batch_id and update existing item
      let batchId = urlBatchIdRef.current;
      
      if (existingItemIdRef.current && !batchId) {
        // Adding to an existing single item - create a batch_id and update the existing item
        batchId = crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        
        // Update the existing item to use the new batch_id
        const { error: updateError } = await supabase
          .from('cellar_tobacco')
          .update({ batch_id: batchId })
          .eq('id', existingItemIdRef.current)
          .eq('user_id', currentUser.id);
        
        if (updateError) {
          throw new Error('Failed to update existing item');
        }
        
        // If no image uploaded, use image from the existing item
        if (!image_url) {
          const { data: existingItem } = await supabase
            .from('cellar_tobacco')
            .select('image_url')
            .eq('id', existingItemIdRef.current)
            .eq('user_id', currentUser.id)
            .single();
          
          if (existingItem && existingItem.image_url) {
            image_url = existingItem.image_url;
          }
        }
      } else if (!batchId) {
        // Create new batch for multiples, or null for single new item
        batchId = qty > 1 ? crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}` : null;
      }

      // If adding to existing batch and no image uploaded, use image from existing batch item
      if (!image_url && batchId && !existingItemIdRef.current) {
        const { data: existingBatchItems } = await supabase
          .from('cellar_tobacco')
          .select('image_url')
          .eq('batch_id', batchId)
          .eq('user_id', currentUser.id)
          .not('image_url', 'is', null)
          .order('created_at', { ascending: false })
          .limit(1);
        
        if (existingBatchItems && existingBatchItems.length > 0 && existingBatchItems[0].image_url) {
          image_url = existingBatchItems[0].image_url;
        }
      }

      const rows = Array.from({ length: qty }).map(() => ({
        user_id: currentUser.id,
        brand: brand.trim(),
        blend_name: blendName.trim(),
        amount: parsedAmount,
        unit,
        purchase_date: purchaseDate,
        tinned_date: tinnedDate || null,
        status,
        storage_type: storageType || null,
        notes: notes.trim() || null,
        image_url,
        batch_id: batchId,
      }));

      const { error } = await supabase
        .from('cellar_tobacco')
        .insert(rows);

      if (error) throw error;
      
      toast.success(qty > 1 ? `Added ${qty} tobaccos to cellar!` : 'Tobacco added to cellar!');
      draftLoadedRef.current = false;
      setDraftLoaded(false);
      await Promise.all([
        clearDraftState(CELLAR_DRAFT_KEY),
      ]);
      clearPhoto();
      setPhotoData(null);
      setPhotoFile(null);
      setPhotoPreview(null);
      navigate('/cellar');
    } catch (err) {
      console.error('Error adding tobacco:', err);
      toast.error('Failed to add tobacco to cellar');
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmitPipe(e: React.FormEvent) {
    e.preventDefault();
    if (!currentUser) return;

    setLoading(true);
    try {
      const image_url = await uploadPhoto('pipes');

      const { error } = await supabase
        .from('cellar_pipes')
        .insert({
          user_id: currentUser.id,
          maker: maker.trim(),
          shape: shape.trim(),
          style: style.trim() || null,
          purchase_date: purchaseDate,
          image_url,
        });

      if (error) throw error;
      
      toast.success('Pipe added to cellar!');
      draftLoadedRef.current = false;
      setDraftLoaded(false);
      await Promise.all([
        clearDraftState(CELLAR_DRAFT_KEY),
      ]);
      clearPhoto();
      setPhotoData(null);
      setPhotoFile(null);
      setPhotoPreview(null);
      navigate('/cellar');
    } catch (err) {
      console.error('Error adding pipe:', err);
      toast.error('Failed to add pipe to cellar');
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmitLighter(e: React.FormEvent, type: 'lighter' | 'matches') {
    e.preventDefault();
    if (!currentUser) return;

    setLoading(true);
    try {
      const image_url = await uploadPhoto('lighters');

      const { error } = await supabase
        .from('cellar_lighters')
        .insert({
          user_id: currentUser.id,
          type,
          brand: lighterBrand.trim(),
          style: lighterStyle.trim() || null,
          colour: colour.trim() || null,
          purchase_date: purchaseDate,
          image_url,
        });

      if (error) throw error;
      
      toast.success('Item added to cellar!');
      draftLoadedRef.current = false;
      setDraftLoaded(false);
      await Promise.all([
        clearDraftState(CELLAR_DRAFT_KEY),
      ]);
      clearPhoto();
      setPhotoData(null);
      setPhotoFile(null);
      setPhotoPreview(null);
      navigate('/cellar');
    } catch (err) {
      console.error('Error adding lighter/matches:', err);
      toast.error('Failed to add item to cellar');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', paddingBottom: '120px' }}>
      {/* Pipe Cropper Modal (2:1) */}
      {showPipeCropper && cropImgEl && (
        <div style={{ position: 'fixed', inset: 0 as any, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '1rem' }}>
          <div style={{ backgroundColor: '#1a1a1a', border: '1px solid var(--color-border)', borderRadius: '12px', width: '100%', maxWidth: '640px', padding: '1rem' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--color-accent)' }}>Position Your Pipe</h3>
            <p style={{ fontSize: '0.875rem', color: '#999', marginBottom: '0.75rem' }}>Pan and zoom so the pipe fits the frame, then tap Use Photo.</p>

            {/* Crop viewport */}
            <div
              style={{
                position: 'relative',
                width: '100%',
                paddingTop: '50%', // 2:1 aspect (height = width*0.5)
                backgroundColor: '#000',
                overflow: 'hidden',
                borderRadius: '8px',
                border: '1px solid var(--color-border)'
              }}
              ref={cropContainerRef}
              onMouseDown={(e) => { setIsPanning(true); setPanStart({ x: e.clientX, y: e.clientY }); }}
              onMouseMove={(e) => {
                if (!isPanning) return;
                if (!panStart) return;
                const dx = e.clientX - panStart.x;
                const dy = e.clientY - panStart.y;
                setPanStart({ x: e.clientX, y: e.clientY });
                setCropOffset(({ x, y }) => ({ x: x + dx, y: y + dy }));
              }}
              onMouseUp={() => setIsPanning(false)}
              onMouseLeave={() => setIsPanning(false)}
              onTouchStart={(e) => { const t = e.touches[0]; setIsPanning(true); setPanStart({ x: t.clientX, y: t.clientY }); }}
              onTouchMove={(e) => {
                if (!isPanning) return;
                const t = e.touches[0];
                if (!panStart) return;
                const dx = t.clientX - panStart.x;
                const dy = t.clientY - panStart.y;
                setPanStart({ x: t.clientX, y: t.clientY });
                setCropOffset(({ x, y }) => ({ x: x + dx, y: y + dy }));
              }}
              onTouchEnd={() => setIsPanning(false)}
            >
              {/* Image element transformed to simulate crop */}
              <img
                src={cropImageUrl || ''}
                alt="Crop"
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: `translate(-50%, -50%) translate(${cropOffset.x}px, ${cropOffset.y}px) scale(${cropBaseScale * cropScale})`,
                  userSelect: 'none',
                  touchAction: 'none',
                  maxWidth: 'none',
                  maxHeight: 'none'
                }}
                draggable={false}
              />
              {/* 2:1 mask border */}
              <div style={{ position: 'absolute', inset: 0, boxShadow: 'inset 0 0 0 2px rgba(196,255,0,0.6)', pointerEvents: 'none', borderRadius: '8px' }} />
            </div>

            {/* Controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.75rem' }}>
              <span style={{ color: '#999', fontSize: '0.875rem' }}>Zoom</span>
              <input
                type="range"
                min={1}
                max={3}
                step={0.01}
                value={cropScale}
                onChange={(e) => setCropScale(parseFloat(e.target.value))}
                style={{ flex: 1 }}
              />
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowPipeCropper(false); setCropImageUrl(null); setCropImgEl(null); }}
                style={{ padding: '0.6rem 1rem', backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', color: '#ccc', borderRadius: '6px', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  // Render cropped area to canvas (export 1000x500)
                  if (!cropImgEl) return;
                  const targetW = 1000; const targetH = 500; // 2:1
                  const canvas = document.createElement('canvas');
                  canvas.width = targetW; canvas.height = targetH;
                  const ctx = canvas.getContext('2d');
                  if (!ctx) return;

                  // Compute draw parameters based on current transform
                  // Use container dimensions to map offsets consistently
                  const rect = cropContainerRef.current?.getBoundingClientRect();
                  const contW = rect?.width || targetW;
                  const contH = rect?.height || targetH;
                  const baseScale = Math.max(targetW / cropImgEl.width, targetH / cropImgEl.height);
                  const scale = baseScale * cropScale;
                  const drawW = cropImgEl.width * scale;
                  const drawH = cropImgEl.height * scale;
                  const dx = targetW / 2 - drawW / 2 + cropOffset.x * (targetW / contW);
                  const dy = targetH / 2 - drawH / 2 + cropOffset.y * (targetH / contH);

                  ctx.fillStyle = '#0b0b0b';
                  ctx.fillRect(0, 0, targetW, targetH);
                  ctx.imageSmoothingQuality = 'high';
                  ctx.drawImage(cropImgEl, dx, dy, drawW, drawH);

                  const blob: Blob | null = await new Promise((res) => canvas.toBlob((b) => res(b), 'image/jpeg', 0.92));
                  if (blob) {
                    const file = new File([blob], 'pipe-crop.jpg', { type: 'image/jpeg' });
                    // Compress lightly to keep consistency with pipeline (but maintain 2:1 bounds)
                    setCompressing(true);
                    try {
                      const compressed = await compressImage(file, {
                        maxWidth: 1000,
                        maxHeight: 500,
                        quality: 0.9,
                        outputFormat: 'image/jpeg',
                      });
                      const r = new FileReader();
                      r.onload = async (ev) => {
                        const result = ev.target?.result as string;
                        if (typeof result === 'string') {
                          const stored = await savePhotoData({
                            name: compressed.name,
                            type: compressed.type || 'image/jpeg',
                            data: result,
                            processed: true,
                            processedAt: new Date().toISOString(),
                          });
                          setPhotoData(stored);
                        }
                      };
                      r.readAsDataURL(compressed);
                    } catch (err) {
                      console.error('Pipe crop compress error:', err);
                      toast.error('Failed to process photo');
                    } finally {
                      setCompressing(false);
                    }
                  }
                  setShowPipeCropper(false);
                  setCropImageUrl(null);
                  setCropImgEl(null);
                }}
                style={{ padding: '0.6rem 1rem', backgroundColor: 'var(--color-accent)', border: 'none', color: '#0b0b0b', borderRadius: '6px', cursor: 'pointer', fontWeight: 700 }}
              >
                Use Photo
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <header style={{
        padding: '1rem',
        borderBottom: '1px solid var(--color-border)',
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', color: 'var(--color-accent)', cursor: 'pointer' }}
        >
          <ArrowLeft size={24} />
        </button>
        <h1 style={{ fontSize: '1.5rem', fontFamily: '"Oswald", sans-serif', letterSpacing: '-0.02em' }}>
          Add to Cellar
        </h1>
      </header>

      <div className="container" style={{ paddingTop: '1rem' }}>
        {!selectedType ? (
          // Type Selection
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <h2 style={{ fontSize: '1.125rem', marginBottom: '0.5rem', color: '#999' }}>
              What would you like to add?
            </h2>

            <button
              onClick={() => setSelectedType('tobacco')}
              className="card"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                padding: '1.25rem',
                cursor: 'pointer',
                transition: 'border-color 0.2s',
                border: '2px solid var(--color-border)',
              }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--color-accent)'}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--color-border)'}
            >
              <div style={{ textAlign: 'left' }}>
                <h3 style={{ fontSize: '1.125rem', marginBottom: '0.25rem' }}>Tobacco</h3>
                <p style={{ fontSize: '0.875rem', color: '#999' }}>Add pipe tobacco or cigars</p>
              </div>
            </button>

            <button
              onClick={() => setSelectedType('pipe')}
              className="card"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                padding: '1.25rem',
                cursor: 'pointer',
                transition: 'border-color 0.2s',
                border: '2px solid var(--color-border)',
              }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--color-accent)'}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--color-border)'}
            >
              <div style={{ textAlign: 'left' }}>
                <h3 style={{ fontSize: '1.125rem', marginBottom: '0.25rem' }}>Pipe</h3>
                <p style={{ fontSize: '0.875rem', color: '#999' }}>Add a pipe to your collection</p>
              </div>
            </button>

            <button
              onClick={() => setSelectedType('lighter')}
              className="card"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                padding: '1.25rem',
                cursor: 'pointer',
                transition: 'border-color 0.2s',
                border: '2px solid var(--color-border)',
              }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--color-accent)'}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--color-border)'}
            >
              <div style={{ textAlign: 'left' }}>
                <h3 style={{ fontSize: '1.125rem', marginBottom: '0.25rem' }}>Lighter</h3>
                <p style={{ fontSize: '0.875rem', color: '#999' }}>Add a lighter or pipe lighter</p>
              </div>
            </button>

            <button
              onClick={() => setSelectedType('matches')}
              className="card"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                padding: '1.25rem',
                cursor: 'pointer',
                transition: 'border-color 0.2s',
                border: '2px solid var(--color-border)',
              }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--color-accent)'}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--color-border)'}
            >
              <div style={{ textAlign: 'left' }}>
                <h3 style={{ fontSize: '1.125rem', marginBottom: '0.25rem' }}>Matches</h3>
                <p style={{ fontSize: '0.875rem', color: '#999' }}>Add matches to your collection</p>
              </div>
            </button>
          </div>
        ) : selectedType === 'tobacco' ? (
          // Tobacco Form
          <form onSubmit={handleSubmitTobacco} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <button
              type="button"
              onClick={() => setSelectedType(null)}
              style={{ 
                alignSelf: 'flex-start', 
                background: 'none', 
                border: 'none', 
                color: 'var(--color-accent)', 
                cursor: 'pointer',
                fontSize: '0.875rem',
              }}
            >
              ← Change type
            </button>

            <div style={{ position: 'relative' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Blender *
              </label>
              <input
                type="text"
                value={brand}
                onChange={(e) => {
                  const v = e.target.value;
                  setBrand(v);
                  setShowBrandSug(true);
                  if (brandTimer.current) window.clearTimeout(brandTimer.current);
                  brandTimer.current = window.setTimeout(async () => {
                    const term = v.trim();
                    if (term.length < 2) { setBrandSuggestions([]); setShowBrandSug(false); return; }
                    const { data, error } = await supabase
                      .from('tobaccos')
                      .select('brand')
                      .ilike('brand', `%${term}%`)
                      .limit(20);
                    if (!error && data) {
                      const uniq = Array.from(new Set(data.map((d: any) => d.brand).filter(Boolean)));
                      setBrandSuggestions(uniq);
                      setShowBrandSug(uniq.length > 0);
                    } else {
                      setBrandSuggestions([]);
                      setShowBrandSug(false);
                    }
                  }, 300);
                }}
                onBlur={() => setTimeout(() => setShowBrandSug(false), 150)}
                onFocus={() => brandSuggestions.length > 0 && setShowBrandSug(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    setShowBrandSug(false);
                  }
                }}
                placeholder="e.g., Cornell & Diehl"
                required
                style={{ width: '100%' }}
              />
              {showBrandSug && brandSuggestions.length > 0 && (
                <div style={{ position: 'absolute', left: 0, right: 0, top: '100%', zIndex: 20, background: 'rgba(11,11,11,0.98)', border: '1px solid var(--color-border)', borderRadius: 6, marginTop: 6, maxHeight: 240, overflowY: 'auto' }}>
                  {brandSuggestions.map((b) => (
                    <div key={b}
                      onClick={() => { setBrand(b); setShowBrandSug(false); }}
                      style={{ padding: '0.6rem 0.75rem', cursor: 'pointer' }}
                      onMouseDown={(e) => e.preventDefault()}
                    >{b}</div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ position: 'relative' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Blend Name *
              </label>
              <input
                type="text"
                value={blendName}
                onChange={(e) => {
                  const v = e.target.value;
                  setBlendName(v);
                  setShowBlendSug(true);
                  if (blendTimer.current) window.clearTimeout(blendTimer.current);
                  blendTimer.current = window.setTimeout(async () => {
                    const term = v.trim();
                    if (term.length < 2) { setBlendSuggestions([]); setShowBlendSug(false); return; }
                    const q = supabase
                      .from('tobaccos')
                      .select('name,brand')
                      .ilike('name', `%${term}%`)
                      .limit(20);
                    const { data, error } = brand.trim().length > 0
                      ? await q.eq('brand', brand)
                      : await q;
                    if (!error && data) {
                      const map = new Map<string, { name: string; brand: string | null }>();
                      data.filter((d: any) => d.name).forEach((d: any) => {
                        const key = (d.name as string).toLowerCase();
                        if (!map.has(key)) {
                          map.set(key, { name: d.name, brand: d.brand ?? null });
                        }
                      });
                      const suggestions = Array.from(map.values());
                      setBlendSuggestions(suggestions);
                      setShowBlendSug(suggestions.length > 0);
                    } else {
                      setBlendSuggestions([]);
                      setShowBlendSug(false);
                    }
                  }, 300);
                }}
                onBlur={() => setTimeout(() => setShowBlendSug(false), 150)}
                onFocus={() => blendSuggestions.length > 0 && setShowBlendSug(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    setShowBlendSug(false);
                  }
                }}
                placeholder="e.g., Opening Night"
                required
                style={{ width: '100%' }}
              />
              {showBlendSug && blendSuggestions.length > 0 && (
                <div style={{ position: 'absolute', left: 0, right: 0, top: '100%', zIndex: 20, background: 'rgba(11,11,11,0.98)', border: '1px solid var(--color-border)', borderRadius: 6, marginTop: 6, maxHeight: 240, overflowY: 'auto' }}>
                  {blendSuggestions.map(({ name: suggestionName, brand: suggestionBrand }) => (
                    <div key={suggestionName}
                      onClick={() => {
                        setBlendName(suggestionName);
                        if (suggestionBrand) {
                          setBrand(suggestionBrand);
                          setBrandSuggestions([]);
                          setShowBrandSug(false);
                        }
                        setShowBlendSug(false);
                      }}
                      style={{ padding: '0.6rem 0.75rem', cursor: 'pointer' }}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      <div style={{ fontWeight: 600 }}>{suggestionName}</div>
                      {suggestionBrand && (
                        <div style={{ fontSize: '0.75rem', color: '#999' }}>{suggestionBrand}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Amount *
                </label>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="50"
                  required
                  step="0.01"
                  min="0"
                  style={{ width: '100%' }}
                />
              </div>

              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Unit *
                </label>
                <select
                  value={unit}
                  onChange={(e) => setUnit(e.target.value as 'grams' | 'ounces')}
                  required
                  style={{ width: '100%' }}
                >
                  <option value="grams">Grams</option>
                  <option value="ounces">Ounces</option>
                </select>
              </div>

              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Multiple
                </label>
                <input
                  type="number"
                  value={multiple}
                  onChange={(e) => {
                    // allow empty while typing, numeric only
                    const next = e.target.value.replace(/[^0-9]/g, '');
                    setMultiple(next);
                  }}
                  onBlur={() => {
                    if (!multiple) setMultiple('1');
                    else {
                      const n = parseInt(multiple, 10);
                      const clamped = Math.max(1, Math.min(isNaN(n) ? 1 : n, 100));
                      setMultiple(String(clamped));
                    }
                  }}
                  min={1}
                  step={1}
                  placeholder="1"
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Purchase Date *
              </label>
              <input
                type="date"
                value={purchaseDate}
                onChange={(e) => setPurchaseDate(e.target.value)}
                required
                style={{ width: '100%' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Tinned Date (Optional)
              </label>
              <input
                type="date"
                value={tinnedDate}
                onChange={(e) => setTinnedDate(e.target.value)}
                style={{ width: '100%' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Storage Type (Optional)
              </label>
              <select
                value={storageType}
                onChange={(e) => setStorageType(e.target.value as any)}
                style={{ width: '100%' }}
              >
                <option value="">None</option>
                <option value="tin">🥫 Tin</option>
                <option value="jar">🫙 Jar</option>
                <option value="pouch">📦 Pouch</option>
                <option value="bulk">📦 Bulk</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: '500' }}>
                Status *
              </label>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <label style={{ 
                  flex: 1,
                  padding: '0.75rem',
                  border: '2px solid',
                  borderColor: status === 'sealed' ? 'var(--color-success)' : 'var(--color-border)',
                  backgroundColor: status === 'sealed' ? 'rgba(107, 175, 74, 0.1)' : 'var(--color-card)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'all 0.2s',
                }}>
                  <input
                    type="radio"
                    name="status"
                    value="sealed"
                    checked={status === 'sealed'}
                    onChange={(e) => setStatus(e.target.value as any)}
                    style={{ display: 'none' }}
                  />
                  <div style={{ fontSize: '0.875rem', fontWeight: status === 'sealed' ? '600' : '400' }}>
                    🟢 Sealed
                  </div>
                </label>

                <label style={{ 
                  flex: 1,
                  padding: '0.75rem',
                  border: '2px solid',
                  borderColor: status === 'opened' ? '#facc15' : 'var(--color-border)',
                  backgroundColor: status === 'opened' ? 'rgba(250, 204, 21, 0.1)' : 'var(--color-card)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'all 0.2s',
                }}>
                  <input
                    type="radio"
                    name="status"
                    value="opened"
                    checked={status === 'opened'}
                    onChange={(e) => setStatus(e.target.value as any)}
                    style={{ display: 'none' }}
                  />
                  <div style={{ fontSize: '0.875rem', fontWeight: status === 'opened' ? '600' : '400' }}>
                    🟡 Opened
                  </div>
                </label>

                <label style={{ 
                  flex: 1,
                  padding: '0.75rem',
                  border: '2px solid',
                  borderColor: status === 'finished' ? '#6b7280' : 'var(--color-border)',
                  backgroundColor: status === 'finished' ? 'rgba(107, 114, 128, 0.1)' : 'var(--color-card)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'all 0.2s',
                }}>
                  <input
                    type="radio"
                    name="status"
                    value="finished"
                    checked={status === 'finished'}
                    onChange={(e) => setStatus(e.target.value as any)}
                    style={{ display: 'none' }}
                  />
                  <div style={{ fontSize: '0.875rem', fontWeight: status === 'finished' ? '600' : '400' }}>
                    ⚪ Finished
                  </div>
                </label>
              </div>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Notes (Optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Tasting notes, storage location, etc."
                rows={3}
                style={{ width: '100%' }}
              />
            </div>

            {/* Photo Upload */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Photo (Optional)
              </label>

              {/* Preview */}
              {photoPreview && (
                <div className="card-photo" style={{ marginBottom: '0.75rem' }}>
                  <img
                    src={photoPreview}
                    alt="Preview"
                    style={{
                      width: '100%',
                      maxHeight: '200px',
                      objectFit: 'contain',
                      borderRadius: '8px',
                      backgroundColor: '#1f1f1f',
                    }}
                  />
                </div>
              )}

              {compressing && (
                <div style={{ 
                  padding: '2rem', 
                  textAlign: 'center',
                  border: '2px dashed #333',
                  borderRadius: '8px',
                  marginBottom: '0.75rem'
                }}>
                  <div className="spinner" style={{ margin: '0 auto 0.5rem', width: '32px', height: '32px' }}></div>
                  <span style={{ color: '#999' }}>Compressing image...</span>
                </div>
              )}
              
              {/* Upload Buttons */}
              {!photoPreview && !compressing && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  <label
                    htmlFor="photo-gallery"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      padding: '1rem',
                      border: '2px dashed #333',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: 'var(--color-card)',
                      transition: 'border-color 0.2s',
                    }}
                  >
                    <ImageIcon size={24} color="var(--color-accent)" />
                    <span style={{ fontSize: '0.875rem', textAlign: 'center' }}>Choose from Gallery</span>
                  </label>

                  <label
                    htmlFor="photo-camera"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      padding: '1rem',
                      border: '2px dashed #333',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: 'var(--color-card)',
                      transition: 'border-color 0.2s',
                    }}
                  >
                    <ImageIcon size={24} color="var(--color-accent)" />
                    <span style={{ fontSize: '0.875rem', textAlign: 'center' }}>Take Photo</span>
                  </label>
                </div>
              )}

              <input
                id="photo-gallery"
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                style={{ display: 'none' }}
              />

              <input
                id="photo-camera"
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoChange}
                style={{ display: 'none' }}
              />
            </div>

              <Button
              type="submit"
              variant="primary"
              loading={loading || compressing}
              fullWidth
            >
              Add to Cellar
            </Button>
          </form>
        ) : selectedType === 'pipe' ? (
          // Pipe Form
          <form onSubmit={handleSubmitPipe} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <button
              type="button"
              onClick={() => setSelectedType(null)}
              style={{ 
                alignSelf: 'flex-start', 
                background: 'none', 
                border: 'none', 
                color: 'var(--color-accent)', 
                cursor: 'pointer',
                fontSize: '0.875rem',
              }}
            >
              ← Change type
            </button>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Maker *
              </label>
              <input
                type="text"
                value={maker}
                onChange={(e) => setMaker(e.target.value)}
                placeholder="e.g., Peterson, Savinelli"
                required
                style={{ width: '100%' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Shape *
              </label>
              <input
                type="text"
                value={shape}
                onChange={(e) => setShape(e.target.value)}
                placeholder="e.g., Billiard, Bulldog"
                required
                style={{ width: '100%' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Style (Optional)
              </label>
              <input
                type="text"
                value={style}
                onChange={(e) => setStyle(e.target.value)}
                placeholder="e.g., Rusticated, Smooth"
                style={{ width: '100%' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Purchase Date *
              </label>
              <input
                type="date"
                value={purchaseDate}
                onChange={(e) => setPurchaseDate(e.target.value)}
                required
                style={{ width: '100%' }}
              />
            </div>

            {/* Photo Upload */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Photo (Optional)
              </label>

              {/* Preview */}
              {photoPreview && (
                <div className="card-photo" style={{ marginBottom: '0.75rem' }}>
                  <img
                    src={photoPreview}
                    alt="Preview"
                    style={{
                      width: '100%',
                      maxHeight: '200px',
                      objectFit: 'contain',
                      borderRadius: '8px',
                      backgroundColor: '#1f1f1f',
                    }}
                  />
                </div>
              )}

              {compressing && (
                <div style={{ 
                  padding: '2rem', 
                  textAlign: 'center',
                  border: '2px dashed #333',
                  borderRadius: '8px',
                  marginBottom: '0.75rem'
                }}>
                  <div className="spinner" style={{ margin: '0 auto 0.5rem', width: '32px', height: '32px' }}></div>
                  <span style={{ color: '#999' }}>Compressing image...</span>
                </div>
              )}
              
              {/* Upload Buttons */}
              {!photoPreview && !compressing && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  <label
                    htmlFor="photo-gallery"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      padding: '1rem',
                      border: '2px dashed #333',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: 'var(--color-card)',
                      transition: 'border-color 0.2s',
                    }}
                  >
                    <ImageIcon size={24} color="var(--color-accent)" />
                    <span style={{ fontSize: '0.875rem', textAlign: 'center' }}>Choose from Gallery</span>
                  </label>

                  <label
                    htmlFor="photo-camera"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      padding: '1rem',
                      border: '2px dashed #333',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: 'var(--color-card)',
                      transition: 'border-color 0.2s',
                    }}
                  >
                    <ImageIcon size={24} color="var(--color-accent)" />
                    <span style={{ fontSize: '0.875rem', textAlign: 'center' }}>Take Photo</span>
                  </label>
                </div>
              )}

              <input
                id="photo-gallery"
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                style={{ display: 'none' }}
              />

              <input
                id="photo-camera"
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoChange}
                style={{ display: 'none' }}
              />
            </div>

              <Button
              type="submit"
              variant="primary"
              loading={loading || compressing}
              fullWidth
            >
              Add to Cellar
            </Button>
          </form>
        ) : (selectedType === 'lighter' || selectedType === 'matches') && (
          // Lighter/Matches Form
          <form 
            onSubmit={(e) => handleSubmitLighter(e, selectedType)} 
            style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}
          >
            <button
              type="button"
              onClick={() => setSelectedType(null)}
              style={{ 
                alignSelf: 'flex-start', 
                background: 'none', 
                border: 'none', 
                color: 'var(--color-accent)', 
                cursor: 'pointer',
                fontSize: '0.875rem',
              }}
            >
              ← Change type
            </button>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Blender *
              </label>
              <input
                type="text"
                value={lighterBrand}
                onChange={(e) => setLighterBrand(e.target.value)}
                placeholder={selectedType === 'lighter' ? "e.g., Zippo, IM Corona" : "e.g., Swan Vestas"}
                required
                style={{ width: '100%' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Style (Optional)
              </label>
              <input
                type="text"
                value={lighterStyle}
                onChange={(e) => setLighterStyle(e.target.value)}
                placeholder={selectedType === 'lighter' ? "e.g., Pipe Lighter" : "e.g., Strike Anywhere"}
                style={{ width: '100%' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Colour (Optional)
              </label>
              <input
                type="text"
                value={colour}
                onChange={(e) => setColour(e.target.value)}
                placeholder="e.g., Brass, Black"
                style={{ width: '100%' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Purchase Date *
              </label>
              <input
                type="date"
                value={purchaseDate}
                onChange={(e) => setPurchaseDate(e.target.value)}
                required
                style={{ width: '100%' }}
              />
            </div>

            {/* Photo Upload */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Photo (Optional)
              </label>

              {/* Preview */}
              {photoPreview && (
                <div className="card-photo" style={{ marginBottom: '0.75rem' }}>
                  <img
                    src={photoPreview}
                    alt="Preview"
                    style={{
                      width: '100%',
                      maxHeight: '200px',
                      objectFit: 'contain',
                      borderRadius: '8px',
                      backgroundColor: '#1f1f1f',
                    }}
                  />
                </div>
              )}

              {compressing && (
                <div style={{ 
                  padding: '2rem', 
                  textAlign: 'center',
                  border: '2px dashed #333',
                  borderRadius: '8px',
                  marginBottom: '0.75rem'
                }}>
                  <div className="spinner" style={{ margin: '0 auto 0.5rem', width: '32px', height: '32px' }}></div>
                  <span style={{ color: '#999' }}>Compressing image...</span>
                </div>
              )}
              
              {/* Upload Buttons */}
              {!photoPreview && !compressing && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  <label
                    htmlFor="photo-gallery"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      padding: '1rem',
                      border: '2px dashed #333',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: 'var(--color-card)',
                      transition: 'border-color 0.2s',
                    }}
                  >
                    <ImageIcon size={24} color="var(--color-accent)" />
                    <span style={{ fontSize: '0.875rem', textAlign: 'center' }}>Choose from Gallery</span>
                  </label>

                  <label
                    htmlFor="photo-camera"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      padding: '1rem',
                      border: '2px dashed #333',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: 'var(--color-card)',
                      transition: 'border-color 0.2s',
                    }}
                  >
                    <ImageIcon size={24} color="var(--color-accent)" />
                    <span style={{ fontSize: '0.875rem', textAlign: 'center' }}>Take Photo</span>
                  </label>
                </div>
              )}

              <input
                id="photo-gallery"
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                style={{ display: 'none' }}
              />

              <input
                id="photo-camera"
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoChange}
                style={{ display: 'none' }}
              />
            </div>

              <Button
              type="submit"
              variant="primary"
              loading={loading || compressing}
              fullWidth
            >
              Add to Cellar
            </Button>
          </form>
        )}
      </div>

      <BottomNav />
    </div>
  );
}

